import { useMemo } from 'react';

import { TableEmptyState } from '@/components/tables/DataTable/components/TableEmptyState';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { useReportError } from '@/hooks/useReportError';

import { createPinnedPresentationTemplateColumn } from '../presentationTemplateTable.columns';
import {
  PresentationTemplateTableVariant,
  presentationTemplateTypeByTableVariant,
} from '../presentationTemplateTable.types';
import {
  DuplicatePresentationTemplateMenuItem,
  UpdatePresentationTemplatePinMenuItem,
} from '../tenantPresentationTemplateTable/TenantPresentationTemplateTable.components';
import {
  SystemPresentationTemplateTableRowData,
  useSystemPresentationTemplateTableData,
} from './SystemPresentationTemplateTable.hooks';

interface SystemPresentationTemplateTableProps {
  alwaysShowPaginationFooter?: boolean;
  variant: PresentationTemplateTableVariant;
  // Opens the duplicated template in the editor.
  onDuplicateComplete: (newTemplateId: string) => void;
  // Opens the clicked system template in the editor. System templates are
  // read-only, so the editor is opened in save-as-new mode (see the callers).
  onRowClick: (row: SystemPresentationTemplateTableRowData) => void;
}

function SystemTemplateTableEmptyState() {
  return <TableEmptyState text="No Luminary templates are available" />;
}

// System templates (e.g. Estate360) are read-only. Clicking a row opens it in
// the editor's save-as-new flow; the overflow menu offers a direct duplicate.
export function SystemPresentationTemplateTable({
  alwaysShowPaginationFooter = false,
  variant,
  onDuplicateComplete,
  onRowClick,
}: SystemPresentationTemplateTableProps) {
  const { rows, loading, error } = useSystemPresentationTemplateTableData();

  useReportError('failed to load system presentation templates', error);

  const columns = useColumns(variant, onDuplicateComplete);

  const slots = useMemo(
    () => ({
      noRowsOverlay: SystemTemplateTableEmptyState,
    }),
    []
  );

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      onRowClick={(params) => onRowClick(params.row)}
      slots={slots}
      pageSize={25}
      alwaysShowPaginationFooter={alwaysShowPaginationFooter}
      hideFooterPagination={!alwaysShowPaginationFooter}
    />
  );
}

function useColumns(
  variant: PresentationTemplateTableVariant,
  onDuplicateComplete: (newTemplateId: string) => void
): Column<SystemPresentationTemplateTableRowData>[] {
  return useMemo(() => {
    const duplicateType = presentationTemplateTypeByTableVariant[variant];
    const showPinning = variant === PresentationTemplateTableVariant.Tenant;

    return [
      {
        field: 'displayName',
        headerName: 'Name',
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        flex: 1,
      },
      {
        field: 'updatedAt',
        headerName: 'Last modified',
        flex: 1,
      },
      ...(showPinning
        ? [
            createPinnedPresentationTemplateColumn<SystemPresentationTemplateTableRowData>(),
          ]
        : []),
      {
        field: 'actions',
        headerName: '',
        renderCell: ActionButtonRenderer({
          actions: ({ row: { id, isPinned } }) => [
            ...(showPinning
              ? [
                  {
                    MenuItem: () => (
                      <UpdatePresentationTemplatePinMenuItem
                        presentationTemplateId={id}
                        isPinned={isPinned}
                      />
                    ),
                  },
                ]
              : []),
            {
              MenuItem: () => (
                <DuplicatePresentationTemplateMenuItem
                  presentationTemplateId={id}
                  duplicateType={duplicateType}
                  onDuplicateComplete={onDuplicateComplete}
                />
              ),
            },
          ],
        }),
      },
    ];
  }, [onDuplicateComplete, variant]);
}
